import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Loader2, KeyRound } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [devToken, setDevToken] = useState("");
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      if (data.dev_token) setDevToken(data.dev_token);
      else setDevToken("__no_account__");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-10 overflow-hidden">
      <div className="absolute inset-0 circuit-bg opacity-25" />
      <div className="absolute inset-0 bg-black/85" />
      <div className="absolute inset-0 nasa-grid-bg opacity-50" />

      <div className="relative w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Logo size={70} />
          <h1 className="mt-5 font-display text-2xl font-bold tracking-wider">
            <span className="text-neon-orange">Zaboravljena</span>{" "}
            <span className="text-neon-green">lozinka</span>
          </h1>
        </div>

        <form onSubmit={onSubmit} className="nasa-card p-8 space-y-5" data-testid="forgot-form">
          <div className="flex items-center gap-3 text-[#22ff77] mb-2">
            <KeyRound className="w-5 h-5" strokeWidth={1.5} />
            <span className="font-display font-semibold tracking-wide">Resetuj lozinku</span>
          </div>
          <p className="text-xs text-zinc-400">
            Unesite email i poslaćemo link za reset lozinke.
          </p>

          <div className="space-y-1">
            <label className="block text-[10px] uppercase tracking-[0.3em] text-zinc-500">Email adresa</label>
            <input
              data-testid="forgot-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-black border border-white/10 px-3 py-3 text-sm focus:border-[#22ff77] focus:outline-none rounded-sm"
              placeholder="vasa@adresa.com"
            />
          </div>

          {err && (
            <div className="border border-red-500/40 bg-red-500/10 text-red-300 text-xs px-3 py-2 rounded-sm">
              {err}
            </div>
          )}

          {devToken && devToken !== "__no_account__" && (
            <div className="border border-[#22ff77]/40 bg-[#22ff77]/5 text-zinc-300 text-xs px-3 py-3 rounded-sm space-y-2">
              <div className="text-[#22ff77] font-semibold">Token za reset (dev):</div>
              <div className="font-mono break-all text-[10px]">{devToken}</div>
              <button
                type="button"
                onClick={() => navigate(`/reset-lozinke?token=${devToken}`)}
                className="w-full bg-[#22ff77] text-black font-bold py-2 text-xs uppercase tracking-widest rounded-sm"
                data-testid="goto-reset"
              >
                Otvori formu za reset
              </button>
            </div>
          )}

          {devToken === "__no_account__" && (
            <div className="border border-[#ff7a18]/40 bg-[#ff7a18]/5 text-zinc-300 text-xs px-3 py-2 rounded-sm">
              Ako nalog postoji, dobićete email sa linkom za reset.
            </div>
          )}

          <button
            data-testid="forgot-submit"
            disabled={loading}
            type="submit"
            className="w-full flex items-center justify-center gap-2 bg-[#22ff77] text-black font-bold py-3 uppercase tracking-[0.2em] text-sm rounded-sm hover:brightness-110 transition-all glow-green disabled:opacity-60"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Pošalji link
          </button>

          <div className="text-center text-xs text-zinc-500 pt-2 border-t border-white/5">
            <Link to="/prijava" className="text-[#ff7a18] hover:underline">Nazad na prijavu</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
